const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/LoginPage/SmsCodeInfo/CircularCountdown.tsx';
import moment, { Moment } from 'moment/moment';
import { Box, CircularProgress, circularProgressClasses, Typography } from '@mui/material';
import { css, cx } from '@emotion/css';
import { Theme, useTheme } from '@mui/material/styles';
import { colors } from '@cistec/common_web/defs/colors';
import { CircularProgressProps } from '@mui/material/CircularProgress/CircularProgress';
import useCountdown from 'App/utils/hooks/countdown';

type CircularTimerDisplayProps = {
    startTime: Moment;
    endTime: Moment;
    className?: string;
};

const getClasses = (theme: Theme) => ({
    wrapper: css`
        display: flex;
        align-items: center;
        gap: 4px; // spacing between text and circle
    `,
    circularProgress: css`
        position: absolute;
        top: 0;
        left: 0;

        & .${circularProgressClasses.circle} {
            stroke-linecap: round;
            stroke: ${theme.palette.primary.main};
        }
    `,
    circularProgressBackground: css`
        color: ${colors.grey[200]};
        position: absolute;
    `,
    timeText: css`
        font-size: 22px;
    `,
});

const formatTime = (seconds: number): string => {
    const duration = moment.duration(seconds, 'seconds');
    const format = duration.asHours() >= 1 ? 'HH:mm:ss' : 'mm:ss';

    return moment(seconds * 1000).format(format);
};

export const CircularCountdown = (props: CircularTimerDisplayProps) => {
    const { startTime, endTime } = props;
    const theme = useTheme();
    const classes = getClasses(theme);
    const totalDuration = moment.duration(endTime.diff(startTime)).asSeconds();

    const { timeLeft, progress } = useCountdown(startTime, totalDuration);

    const circularProgressProps: CircularProgressProps = {
        size: 22,
        thickness: 8,
        variant: 'determinate',
    };

    return (
        <Box className={cx(classes.wrapper, props.className)}>
            <Box
                sx={{
                    position: 'relative',
                    width: 24,
                    height: 24,
                }}
            >
                <CircularProgress
                    {...circularProgressProps}
                    value={100}
                    className={classes.circularProgressBackground}
                />
                <CircularProgress
                    {...circularProgressProps}
                    value={100 - progress}
                    className={classes.circularProgress}
                />
            </Box>
            <Typography variant="h6" component="div" sx={{ color: 'text.secondary' }} className={classes.timeText}>
                {formatTime(Math.round(timeLeft))}
            </Typography>
        </Box>
    );
};
